import React, { useEffect, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Typography,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CalendarMonth as CalendarIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { RequireIcon as CustomRequireIcon, CustomCloseIcon } from "@ippon/shared-assets";
import { useSharedColors } from "../../theme/SharedComponentsTheme";
import "./CustomTextInput.css";

// CustomTextField スタイル定義
const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    height: "32px",
    backgroundColor: "#ffffff",
    [theme.breakpoints.up("sm")]: {
      height: "34px",
      borderRadius: "8px",
    },
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#D9D9D9",
    },
    "&:hover fieldset": {
      borderColor: "#D9D9D9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#D9D9D9 !important",
    },
    "&.Mui-focused": {
      outline: "none",
      boxShadow: "none",
    },
    "&.Mui-disabled": (ownerState: any) => ({
      backgroundColor: `${
        (ownerState as any)?.colors?.background?.disabled
      } !important`,
      "& fieldset": {
        borderColor: `${
          (ownerState as any)?.colors?.border?.medium
        } !important`,
      },
    }),
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 12px",
    fontSize: "12px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "12px",
      padding: "8px 12px",
    },
    "&::placeholder": (ownerState: any) => ({
      fontSize: "12px",
      opacity: 0.8,
      [theme.breakpoints.up("sm")]: {
        fontSize: "12px",
      },
      color: (ownerState as any)?.colors?.text?.placeholder,
    }),
    "&.Mui-disabled": (ownerState: any) => ({
      color: `${(ownerState as any)?.colors?.text?.primary} !important`,
      WebkitTextFillColor: `${
        (ownerState as any)?.colors?.text?.primary
      } !important`,
    }),
  },
  "@media (max-width: 600px)": {
    "& .MuiOutlinedInput-input": {
      fontSize: "12px",
      "&::placeholder": {
        fontSize: "12px",
      },
    },
  },
}));

// CustomTextAreaField スタイル定義
// MUI multiline のデフォルト padding (16.5px 14px) は単行入力と並べたとき
// 上下に余白が出すぎて視覚的に違和感が出るので、単行と同じ 8px 12px に揃える。
const CustomTextAreaField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    padding: "8px 12px",
    [theme.breakpoints.up("sm")]: {
      borderRadius: "8px",
      padding: "8px 12px",
    },
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#D9D9D9",
    },
    "&:hover fieldset": {
      borderColor: "#D9D9D9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#D9D9D9 !important",
    },
    "&.Mui-focused": {
      outline: "none",
      boxShadow: "none",
    },
    "&.Mui-disabled": (ownerState: any) => ({
      backgroundColor: `${
        (ownerState as any)?.colors?.background?.disabled
      } !important`,
      "& fieldset": {
        borderColor: `${
          (ownerState as any)?.colors?.border?.medium
        } !important`,
      },
    }),
  },
  "& .MuiOutlinedInput-input": {
    padding: 0,
    fontSize: "12px",
    "&::placeholder": (ownerState: any) => ({
      fontSize: "12px",
      opacity: 0.8,
      color: (ownerState as any)?.colors?.text?.placeholder,
    }),
    "&.Mui-disabled": (ownerState: any) => ({
      color: `${(ownerState as any)?.colors?.text?.primary} !important`,
      WebkitTextFillColor: `${
        (ownerState as any)?.colors?.text?.primary
      } !important`,
    }),
  },
}));

/** 円(showYen)欄: blur 時に全角等を数値化。解釈不能なら null（変更不要で直前の値に戻る） */
function parseYenOnBlur(input: string): { value: string } | null {
  const compact = input
    .normalize("NFKC")
    .trim()
    .replace(/[,，\s\u3000]/g, "");
  if (compact === "") {
    return { value: "" };
  }
  const n = Number(compact);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return { value: String(n) };
}

export type InputModeType =
  | "text"
  | "none"
  | "tel"
  | "url"
  | "email"
  | "numeric"
  | "decimal"
  | "search";

export interface CustomTextInputProps {
  label: string;
  name: string;
  placeholder?: string;
  register: any;
  errors: any;
  disabled?: boolean;
  isRequired?: boolean;
  /** isRequired のときに使う React Hook Form の required メッセージ。
   *  省略時は `${label}は必須です`。 */
  requiredMessage?: string;
  type?: string;
  rows?: number;
  multiline?: boolean;
  min?: number;
  max?: number;
  minMessage?: string;
  maxMessage?: string;
  labelWidth?: string;
  labelWidthSp?: string;
  inputWidth?: string;
  inputWidthSp?: string;
  labelSx?: any;
  /** ラベル文字列の右隣に挟む任意の React ノード (AI バッジ等) */
  labelSuffix?: React.ReactNode;
  showYen?: boolean;
  showManYen?: boolean; // 万円
  showMeter?: boolean;
  showTsubo?: boolean; // 坪
  showPercent?: boolean; // %
  showYearIcon?: boolean;
  endAdornmentText?: string;
  showCalendarIcon?: boolean;
  onCalendarClick?: () => void;
  showMinute?: boolean;
  children?: React.ReactNode;
  autoSetAddress?: (e: React.FocusEvent<HTMLInputElement>) => void;
  watch?: any;
  setValue?: any;
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
  step?: string;
  showSearchIconStart?: boolean;
  search?: boolean;
  hasSpacing?: boolean;
  sx?: any;
  inputMode?: InputModeType;
  showRemoveButton?: boolean;
  onRemoveClick?: () => void;
  isModalInput?: boolean;
}

const CustomTextInput = ({
  label,
  name,
  placeholder = "",
  register,
  errors,
  disabled = false,
  isRequired = false,
  requiredMessage,
  type = "text",
  rows,
  multiline = false,
  min,
  max,
  minMessage,
  maxMessage,
  labelWidth,
  labelWidthSp,
  inputWidth,
  inputWidthSp,
  labelSx = {},
  labelSuffix,
  showYen = false,
  showManYen = false,
  showMeter = false,
  showTsubo = false,
  showPercent = false,
  showYearIcon = false,
  endAdornmentText,
  showCalendarIcon,
  onCalendarClick,
  showMinute = false,
  children,
  autoSetAddress,
  watch,
  setValue,
  autoResize = false,
  minRows = 3,
  maxRows = 30,
  step = "any",
  showSearchIconStart = false,
  search = false,
  hasSpacing,
  sx,
  inputMode,
  showRemoveButton = false,
  onRemoveClick,
  isModalInput = false,
}: CustomTextInputProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = useSharedColors();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // 3桁ごとにカンマ
  function formatNumberWithCommas(value: string | number) {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toLocaleString();
  }

  // 価格用の表示値（useMemoで計算）
  const watchedValue = watch && name ? watch(name) : undefined;
  const displayValue = React.useMemo(() => {
    if (type === "number" && showYen && watch && name) {
      return formatNumberWithCommas(watchedValue ?? "");
    }
    return "";
  }, [type, showYen, watch, name, watchedValue]);

  // 円: フォーカス中は何でも入力、blur で正規化して setValue
  const [yenEditDraft, setYenEditDraft] = React.useState<string | null>(null);

  // showYen + type=number パスでは独自の handler を使うため通常の register をスキップ
  // しているが、その場合 RHF にバリデーションルール (required 等) が登録されないため、
  // useEffect で明示的に register を呼んでルールだけ登録する。値は handleYenBlur 内の
  // setValue で同期されているので競合しない。
  React.useEffect(() => {
    if (type === "number" && showYen && watch && setValue && register) {
      register(name, getValidationRules());
    }
    // getValidationRules は毎レンダー新規生成だが依存に入れると毎回 register が
    // 呼ばれてしまうため、必須/長さ系の主要パラメータのみを依存にする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, type, showYen, isRequired, requiredMessage, min, max]);

  const handleYenFocus = () => {
    setYenEditDraft(String(watchedValue ?? ""));
  };
  const handleYenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setYenEditDraft(e.target.value);
  };
  const handleYenBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!setValue) {
      setYenEditDraft(null);
      return;
    }
    const raw = e.target.value;
    const parsed = parseYenOnBlur(raw);
    if (parsed === null) {
      setYenEditDraft(null);
      return;
    }
    setValue(name, parsed.value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setYenEditDraft(null);
  };

  // テキストエリアの自動リサイズとスクロール処理
  const handleTextAreaChange = useCallback(
    (_e: React.ChangeEvent<HTMLTextAreaElement>, shouldScroll = true) => {
      if (autoResize && textAreaRef.current) {
        const textArea = textAreaRef.current;

        // 高さをリセットして正確な scrollHeight を取得
        textArea.style.height = "auto";

        // 行数を計算
        const lineHeight = parseInt(getComputedStyle(textArea).lineHeight);
        const padding =
          parseInt(getComputedStyle(textArea).paddingTop) +
          parseInt(getComputedStyle(textArea).paddingBottom);
        const minHeight = lineHeight * minRows + padding;
        const maxHeight = lineHeight * maxRows + padding;

        // 新しい高さを設定（最小・最大値の範囲内）
        const newHeight = Math.min(
          Math.max(textArea.scrollHeight, minHeight),
          maxHeight
        );
        textArea.style.height = `${newHeight}px`;

        // スクロール処理はユーザーの入力時のみ実行
        if (shouldScroll) {
          setTimeout(() => {
            const textAreaRect = textArea.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const bottomOffset = 150; // 下部に150pxの余裕を追加（fixed要素対応）

            // テキストエリアの下端が画面下部から150px以上上にあるかチェック
            if (textAreaRect.bottom > viewportHeight - bottomOffset) {
              // カスタムスクロール: テキストエリアの下に余裕を持たせる
              const scrollAmount =
                textAreaRect.bottom - (viewportHeight - bottomOffset);
              window.scrollBy({
                top: scrollAmount,
                behavior: "smooth",
              });
            }
          }, 100);
        }
      }
    },
    [autoResize, minRows, maxRows]
  );

  const getValidationRules = () => {
    const rules: any = {};

    if (isRequired) {
      rules.required = requiredMessage ?? `${label}は必須です`;
    }

    if (type === "number" && (min !== undefined || max !== undefined)) {
      rules.validate = {
        digitsCheck: (value: string) => {
          const numStr = value.toString().replace(/[-.]/g, "");
          if (min !== undefined && numStr.length < min) {
            return minMessage || `Number must have at least ${min} digit(s)`;
          }
          if (max !== undefined && numStr.length > max) {
            return maxMessage || `Number must not exceed ${max} digit(s)`;
          }
          return true;
        },
      };
    }

    if (type === "email") {
      rules.pattern = {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "有効なメールアドレスを入力してください",
      };
    }

    if (type === "phoneNumber") {
      rules.pattern = {
        value: /^\d{10,11}$/,
        message: "電話番号は10桁または11桁の数字である必要があります",
      };
    }

    return rules;
  };

  // register関数の戻り値を取得（multilineの場合のみ）
  const fieldRegistration = multiline
    ? register(name, getValidationRules())
    : null;

  // テキストエリア用の各種イベントハンドラ
  const combinedOnChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // react-hook-formの元のonChangeを実行
      if (fieldRegistration?.onChange) {
        fieldRegistration.onChange(e);
      }
      // 自動リサイズ処理
      if (autoResize) {
        handleTextAreaChange(e);
      }
    },
    [fieldRegistration, autoResize, handleTextAreaChange]
  );

  const combinedOnInputHandler = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      // 自動リサイズ処理（onInputイベント用）
      if (autoResize) {
        handleTextAreaChange(e as React.ChangeEvent<HTMLTextAreaElement>);
      }
    },
    [autoResize, handleTextAreaChange]
  );

  const combinedOnPasteHandler = useCallback(
    (_e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // ペースト後に少し遅延させてリサイズ実行（ペーストされたコンテンツが反映されるのを待つ）
      if (autoResize) {
        setTimeout(() => {
          if (textAreaRef.current) {
            handleTextAreaChange({
              target: textAreaRef.current,
            } as React.ChangeEvent<HTMLTextAreaElement>);
          }
        }, 10);
      }
    },
    [autoResize, handleTextAreaChange]
  );

  // 初期レンダリング時にテキストエリアの高さを設定
  useEffect(() => {
    if (autoResize && multiline && textAreaRef.current) {
      // 初期高さをminRowsに設定
      const textArea = textAreaRef.current;
      const lineHeight = parseInt(getComputedStyle(textArea).lineHeight);
      const padding =
        parseInt(getComputedStyle(textArea).paddingTop) +
        parseInt(getComputedStyle(textArea).paddingBottom);
      const initialHeight = lineHeight * minRows + padding;
      textArea.style.height = `${initialHeight}px`;
    }
  }, [autoResize, multiline, minRows]);

  // 値の変化を監視してテキストエリアのサイズを調整
  useEffect(() => {
    if (autoResize && multiline && textAreaRef.current && watch && name) {
      const currentValue = watch(name);
      // 値が存在する場合（空文字でも実行）
      if (currentValue !== undefined && currentValue !== null) {
        // 少し遅延させてDOMの更新を待つ
        setTimeout(() => {
          if (textAreaRef.current) {
            handleTextAreaChange(
              {
                target: textAreaRef.current,
              } as React.ChangeEvent<HTMLTextAreaElement>,
              false
            ); // スクロール無効
          }
        }, 0);
      }
    }
  }, [watch && watch(name), autoResize, multiline, handleTextAreaChange, name]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      if (e.key === "e" || e.key === "E") e.preventDefault();
      if (e.key === "-") e.preventDefault();
      // 小数点は許可（既に許可されているが、明示的にコメントで示す）
    }
  };

  const getResponsiveClass = () => {
    return "flexCol";
  };

  return (
    <Box
      className={`inputGroupWrapper ${getResponsiveClass()} ${
        isMobile ? "sp" : ""
      } ${multiline && "multiline"} ${showCalendarIcon ? "hasCalendar" : ""} ${
        hasSpacing === false ? "noSpacing" : ""
      }`}
      // AI 画面コンテキスト用 (getPageContext が拾う)。
      // 呼び出し側でラッパーを追加しなくても、name/label があれば自動的に data-field/data-label を出力する。
      data-field={name || undefined}
      data-label={label || undefined}
      sx={{
        display: "flex !important",
        flexDirection: "column !important",
        width: "100%",
        ...sx,
      }}
    >
      {(label || isRequired) && (
        <Typography
          className={`inputLabel ${isMobile ? "sp" : ""} ${
            multiline && "multiline"
          }`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: { lg: labelWidth, xs: labelWidthSp || "100%" },
            fontSize: "12px !important",
            alignSelf: "flex-start",
            paddingTop: 0,
            paddingBottom: "4px",
            marginBottom: "1px",
            textAlign: "left",
            justifyContent: "flex-start",
            ...labelSx,
          }}
        >
          {label}
          {isRequired && <CustomRequireIcon />}
          {labelSuffix}
        </Typography>
      )}
      <Box
        className={`inputGroupWrapperWidth ${isModalInput ? "modalInput" : ""}`}
        sx={{
          width: "100%",
          maxWidth: { sm: inputWidth, xs: inputWidthSp },
        }}
      >
        {multiline ? (
          <CustomTextAreaField
            fullWidth
            type={type}
            placeholder={placeholder}
            {...(fieldRegistration
              ? {
                  ...fieldRegistration,
                  onChange: combinedOnChangeHandler,
                }
              : register(name, getValidationRules()))}
            error={!!(errors as any)[name as string]}
            helperText={
              typeof (errors as any)[name as string]?.message === "string"
                ? (errors as any)[name as string]?.message
                : ""
            }
            disabled={disabled}
            {...(autoResize ? { minRows, maxRows } : { rows })}
            multiline={multiline}
            inputRef={textAreaRef}
            onInput={
              autoResize
                ? (e: React.FormEvent<HTMLDivElement>) =>
                    combinedOnInputHandler(e as any)
                : undefined
            }
            onPaste={
              autoResize
                ? (e: React.ClipboardEvent<HTMLDivElement>) =>
                    combinedOnPasteHandler(e as any)
                : undefined
            }
            sx={{
              ...(autoResize && {
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-start",
                  // 初期高さをminRowsに固定
                  minHeight: `${1.4 * minRows + 2}em`,
                },
                "& .MuiOutlinedInput-input": {
                  resize: "none",
                  overflow: "hidden",
                  minHeight: `${1.4 * minRows}em`,
                  maxHeight: `${1.4 * maxRows}em`,
                  // 初期状態でmaxRowsの高さにならないよう制御
                  height: `${1.4 * minRows}em`,
                },
              }),
            }}
          />
        ) : (
          <CustomTextField
            fullWidth
            type={type === "number" && showYen ? "text" : type}
            placeholder={placeholder}
            {...(type === "number" && showYen && watch && setValue
              ? {
                  name,
                  value: yenEditDraft !== null ? yenEditDraft : displayValue,
                  onChange: handleYenChange,
                  onFocus: handleYenFocus,
                  onBlur: handleYenBlur,
                }
              : register(name, {
                  ...getValidationRules(),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (
                      (name === "postal_code" ||
                        name === "head_office_postcode" ||
                        name === "representative_postcode" ||
                        name === "manager_postcode" ||
                        name === "guarantor_postal_code") &&
                      typeof autoSetAddress === "function"
                    ) {
                      const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                      if (digitsOnly.length === 7) {
                        autoSetAddress(e as React.FocusEvent<HTMLInputElement>);
                      }
                    }
                  },
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    if (
                      (name === "postal_code" ||
                        name === "head_office_postcode" ||
                        name === "representative_postcode" ||
                        name === "manager_postcode" ||
                        name === "guarantor_postal_code") &&
                      typeof autoSetAddress === "function"
                    ) {
                      autoSetAddress(e);
                    }
                  },
                }))}
            error={!!(errors as any)[name as string]}
            helperText={
              typeof (errors as any)[name as string]?.message === "string"
                ? (errors as any)[name as string]?.message
                : ""
            }
            disabled={disabled}
            onKeyDown={handleKeyDown}
            inputProps={{
              inputMode:
                inputMode ?? (type === "number" ? "numeric" : undefined),
              step: type === "number" ? step : undefined,
            }}
            InputProps={{
              startAdornment: showSearchIconStart ? (
                <InputAdornment position="start" sx={{ mr: 0, ml: -0.5 }}>
                  <SearchIcon sx={{ color: "#989898", fontSize: 20 }} />
                </InputAdornment>
              ) : undefined,
              endAdornment: showRemoveButton ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={onRemoveClick}
                    edge="end"
                    color="error"
                    sx={{ width: "30px", padding: "10px" }}
                  >
                    <CustomCloseIcon />
                  </IconButton>
                </InputAdornment>
              ) : endAdornmentText ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    {endAdornmentText}
                  </Typography>
                </InputAdornment>
              ) : showManYen ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    万円
                  </Typography>
                </InputAdornment>
              ) : showYen ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    円
                  </Typography>
                </InputAdornment>
              ) : showMeter ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    ㎡
                  </Typography>
                </InputAdornment>
              ) : showTsubo ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    坪
                  </Typography>
                </InputAdornment>
              ) : showPercent ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    %
                  </Typography>
                </InputAdornment>
              ) : showCalendarIcon ? (
                <InputAdornment position="end">
                  <IconButton onClick={onCalendarClick} sx={{ padding: "0" }}>
                    <CalendarIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </InputAdornment>
              ) : showYearIcon ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    年
                  </Typography>
                </InputAdornment>
              ) : showMinute ? (
                <InputAdornment position="end">
                  <Typography
                    sx={{ fontSize: "12px", color: colors.text.primary }}
                  >
                    分
                  </Typography>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              ...(search && {
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px 8px",
                },
              }),
              ...(type === "number" && {
                "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                  {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }),
            }}
          />
        )}
        {children && children}
      </Box>
    </Box>
  );
};

export default CustomTextInput;
